/**
 * features/exams/ExamRegistersPane.tsx — the register, filled in the hall.
 * ------------------------------------------------------------------
 * Phase 3 of the Exams section. It replaces a printed sheet that the office
 * types up afterwards, so it is built for the person standing at the front of
 * a room with a phone, not for a desk:
 *
 *   EVERYONE STARTS PRESENT and absentees are marked (the school,
 *   2026-09-18) — two taps in a room of thirty rather than thirty. The cost
 *   of that default is that an untouched register looks like a full house, so
 *   an unconfirmed one is shown as NOT TAKEN everywhere and counts as nothing
 *   until somebody confirms it.
 *
 *   THE LIST IS THE STATEMENTS OF ENTRY, and the office's own count sits
 *   beside it. They disagree on 15 papers of the live series, and two rota
 *   rows are mis-coded outright; a register that quietly picked one source
 *   would hide exactly the thing worth finding in September.
 *
 *   CONFIRMING IS A SIGNATURE. It stamps the name and the time, freezes the
 *   totals, and locks the register to its invigilator — the office can
 *   reopen it, and that is stamped too. The rules enforce all of that; this
 *   screen only offers what they will allow (`canFillRegister`).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ClipboardCheck, Clock, Lock, MessageSquare, Search, Undo2, UserX,
} from 'lucide-react';
import { collection, doc, getDocs, query, where } from 'firebase/firestore';
import { db, updateDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import {
  PanelHeader, SoftCard, StatChip, StatStrip, btnGhost, btnPrimary, glassFieldClass,
} from '@/components/ui/design';
import {
  canFillRegister, candidatesForPaper, entryDiscrepancy, isLocked, registerState, registerTotals,
  type ExamRegister, type RegisterCandidate, type RegisterMark,
} from '@/lib/examRegister';
import { seatingId, type Seat } from '@/lib/examSeating';
import { gaboroneInstant, type PupilExamEntries } from '@/lib/examTimetable';
import { shortDay } from './ExamsHub';

interface Candidate extends PupilExamEntries { className?: string }

export default function ExamRegistersPane({ seriesId, now, openId }: {
  seriesId: string; now: number; openId?: string;
}) {
  const { permissions, userData } = useAuth();
  const { students } = useData();
  const [registers, setRegisters] = useState<ExamRegister[]>([]);
  const [entries, setEntries] = useState<PupilExamEntries[]>([]);
  /* The seating plan for each session, so a register can say WHERE a
     candidate sits. Phase 4 chose to keep the register per paper and show
     the room on it, rather than splitting registers by room. */
  const [seating, setSeating] = useState<Record<string, Record<string, Seat>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(openId ?? '');

  const isManager = permissions.canManageExamTimetables;
  const myTeacherId = userData?.linkedTeacherId ?? '';

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [regSnap, entrySnap, seatSnap] = await Promise.all([
          getDocs(query(collection(db, 'reportExamRegisters'), where('seriesId', '==', seriesId))),
          getDocs(query(collection(db, 'reportExamEntries'), where('seriesId', '==', seriesId))),
          getDocs(query(collection(db, 'reportExamSeating'), where('seriesId', '==', seriesId))),
        ]);
        if (!live) return;
        setRegisters(regSnap.docs.map((d) => ({ ...(d.data() as ExamRegister), id: d.id })));
        setEntries(entrySnap.docs.map((d) => d.data() as PupilExamEntries));
        setSeating(Object.fromEntries(seatSnap.docs.map((d) => [d.id, (d.data().seats ?? {}) as Record<string, Seat>])));
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'Could not load the registers.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [seriesId]);

  /* A candidate's class comes from the pupil record: a statement of entry
     names a centre candidate and knows nothing about Form 5A. */
  const candidates = useMemo<Candidate[]>(() => {
    const classOf = new Map((students ?? []).map((s) => [s.id, s.currentClass || '']));
    return entries.map((e) => ({ ...e, className: classOf.get(e.studentId) || '' }));
  }, [entries, students]);

  const rows = useMemo(() => registers
    .map((register) => ({
      register,
      list: candidatesForPaper(candidates, register.code, register.date, register.session),
      started: gaboroneInstant(register.date, register.start || '00:00') <= now,
      mine: (register.invigilatorIds ?? []).includes(myTeacherId),
    }))
    .sort((a, b) => a.register.date.localeCompare(b.register.date)
      || a.register.session.localeCompare(b.register.session)
      || a.register.code.localeCompare(b.register.code)), [registers, candidates, now, myTeacherId]);

  const save = async (id: string, patch: Record<string, unknown>) => {
    const before = registers;
    setRegisters((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } as ExamRegister : r)));
    try {
      await updateDoc(doc(db, 'reportExamRegisters', id), {
        ...patch, updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? '',
      });
      setError('');
    } catch (e) {
      setRegisters(before);
      setError(e instanceof Error ? e.message : 'That change was not saved.');
    }
  };

  if (loading) return <p className="py-10 text-sm text-[var(--sp-muted)]">Loading the registers…</p>;
  if (!registers.length) {
    return (
      <SoftCard pad="">
        <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">
          No registers have been opened for this series yet — run <code>seed/seed-exam-registers.cjs</code>.
        </p>
      </SoftCard>
    );
  }

  const current = rows.find((r) => r.register.id === open) ?? null;
  const taken = rows.filter((r) => registerState(r.register) === 'taken').length;
  const late = rows.filter((r) => r.started && registerState(r.register) === 'notTaken').length;
  const mine = rows.filter((r) => r.mine && registerState(r.register) === 'notTaken');
  const absences = rows.reduce((n, r) => n
    + Object.values(r.register.marks ?? {}).filter((m) => m.status === 'absent').length, 0);

  return (
    <div className="space-y-5">
      <StatStrip>
        <StatChip tone="navy" icon={ClipboardCheck} value={`${taken}/${rows.length}`} label="registers taken" />
        <StatChip
          tone={late ? 'rose' : 'emerald'} icon={late ? AlertTriangle : CheckCircle2}
          value={late} label={late ? 'started, not yet taken' : 'nothing outstanding'}
        />
        <StatChip tone="amber" icon={UserX} value={absences} label="absences recorded" />
        <StatChip tone="sky" value={mine.length} label="still to take, yours" />
      </StatStrip>

      {error && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </p>
      )}

      {current ? (
        <RegisterSheet
          register={current.register}
          list={current.list}
          seats={seating[seatingId(seriesId, current.register.date, current.register.session)] ?? {}}
          canFill={canFillRegister(current.register, myTeacherId, isManager)}
          isManager={isManager}
          userName={userData?.displayName || userData?.email || 'a member of staff'}
          uid={userData?.uid ?? ''}
          onBack={() => setOpen('')}
          onSave={(patch) => { void save(current.register.id, patch); }}
        />
      ) : (
        <PaperList rows={rows} onOpen={setOpen} myTeacherId={myTeacherId} />
      )}
    </div>
  );
}

/* ── the papers ───────────────────────────────────────────────────── */

interface Row {
  register: ExamRegister;
  list: RegisterCandidate[];
  started: boolean;
  mine: boolean;
}

function PaperList({ rows, onOpen, myTeacherId }: {
  rows: Row[]; onOpen: (id: string) => void; myTeacherId: string;
}) {
  const [showDone, setShowDone] = useState(false);
  const visible = showDone ? rows : rows.filter((r) => registerState(r.register) === 'notTaken');
  const byDay = new Map<string, Row[]>();
  for (const row of visible) {
    byDay.set(row.register.date, [...(byDay.get(row.register.date) ?? []), row]);
  }

  return (
    <SoftCard pad="">
      <PanelHeader
        icon={ClipboardCheck}
        title={showDone ? 'Every paper' : 'Registers still to take'}
        subtitle={myTeacherId ? 'Yours are marked — the office sees them all' : 'One register per paper'}
        right={(
          <button type="button" className="text-sm font-semibold text-primary hover:underline" onClick={() => setShowDone((v) => !v)}>
            {showDone ? 'Only what is outstanding' : 'Show every paper'}
          </button>
        )}
      />
      {!visible.length && (
        <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">
          Every register has been taken.
        </p>
      )}
      <ul className="divide-y divide-[var(--sp-line)]">
        {[...byDay.entries()].map(([date, dayRows]) => (
          <li key={date} className="px-4 py-3">
            <p className="mb-2 text-sm font-bold text-ink dark:text-slate-100">{shortDay(date)}</p>
            <ul className="space-y-1.5">
              {dayRows.map((row) => {
                const done = registerState(row.register) === 'taken';
                const overdue = !done && row.started;
                const totals = registerTotals(row.list, row.register.marks ?? {});
                return (
                  <li key={row.register.id}>
                    <button
                      type="button" onClick={() => onOpen(row.register.id)}
                      className="flex w-full flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-lg border border-[var(--sp-line)] bg-[var(--sp-paper)] px-3 py-2 text-left hover:border-[var(--sp-line-2)]"
                    >
                      <span className="min-w-0">
                        <span className="text-sm font-bold text-ink dark:text-slate-100">{row.register.code}</span>{' '}
                        <span className="text-sm text-[var(--sp-body)]">{row.register.subject}</span>
                        <span className="text-xs text-[var(--sp-muted)]">
                          {' · '}{row.register.session || 'test window'}
                          {row.register.start ? ` ${row.register.start}` : ''}
                          {' · '}{row.list.length} entered
                        </span>
                        {row.mine && (
                          <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800 dark:bg-violet-950/60 dark:text-violet-300">
                            yours
                          </span>
                        )}
                      </span>
                      <span className={`whitespace-nowrap text-xs font-semibold ${
                        done ? 'text-emerald-700 dark:text-emerald-400'
                          : overdue ? 'text-rose-600 dark:text-rose-400' : 'text-[var(--sp-muted)]'}`}
                      >
                        {done
                          ? `taken · ${(row.register.totals?.absent ?? totals.absent) || 'no'} absent`
                          : overdue ? 'not taken — the paper has started' : 'not taken'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </SoftCard>
  );
}

/* ── one register ─────────────────────────────────────────────────── */

function RegisterSheet({ register, list, seats, canFill, isManager, userName, uid, onBack, onSave }: {
  register: ExamRegister;
  list: RegisterCandidate[];
  seats: Record<string, Seat>;
  canFill: boolean;
  isManager: boolean;
  userName: string;
  uid: string;
  onBack: () => void;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [find, setFind] = useState('');
  const [noteFor, setNoteFor] = useState('');
  const [room, setRoom] = useState('');
  const rooms = [...new Set(list.map((c) => seats[c.studentId]?.roomName).filter(Boolean))] as string[];
  const marks = register.marks ?? {};
  const locked = isLocked(register);
  const totals = registerTotals(list, marks);
  const gap = entryDiscrepancy(list.length, register.officeCount);

  const setMark = (studentId: string, mark: RegisterMark | null) => {
    const next = { ...marks };
    if (mark) next[studentId] = mark; else delete next[studentId];
    onSave({ marks: next });
  };

  const shown = list.filter((c) => {
    const needle = find.trim().toLowerCase();
    const matches = !needle || c.candidateName.toLowerCase().includes(needle) || c.candidateNumber.includes(needle);
    return matches && (!room || seats[c.studentId]?.roomName === room);
  });

  return (
    <SoftCard pad="">
      <div className="border-b border-[var(--sp-line)] px-4 py-3">
        <button type="button" onClick={onBack} className="text-sm font-semibold text-primary hover:underline">
          ← All papers
        </button>
        <h2 className="mt-1 text-base font-bold text-ink dark:text-slate-100">
          {register.code} {register.subject}
          {register.component && <span className="font-normal text-[var(--sp-muted)]"> · {register.component}</span>}
        </h2>
        <p className="text-sm text-[var(--sp-muted)]">
          {shortDay(register.date)} · {register.session || 'test window'}
          {register.start ? ` · ${register.start}–${register.end}` : ''} · {list.length} entered
        </p>
        {gap && (
          <p className="mt-2 inline-flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{gap}
          </p>
        )}
      </div>

      {locked && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--sp-line)] bg-[var(--sp-paper)] px-4 py-2.5">
          <p className="inline-flex items-center gap-1.5 text-sm text-[var(--sp-body)]">
            <Lock className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
            Taken by {register.confirmedByName || 'a member of staff'} · {when(register.confirmedAt)}
            {register.reopenedAt && <span className="text-[var(--sp-muted)]"> · reopened {when(register.reopenedAt)}</span>}
          </p>
          {isManager && (
            <button
              type="button" className={`${btnGhost} px-3 py-1.5 text-xs`}
              onClick={() => onSave({
                confirmedAt: '', confirmedBy: '', confirmedByName: '',
                reopenedAt: new Date().toISOString(), reopenedByName: userName,
              })}
            >
              <Undo2 className="h-3.5 w-3.5" /> Reopen
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-[var(--sp-muted)]" />
            <input
              value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a candidate"
              className={glassFieldClass('w-56 pl-8')}
            />
          </label>
          {rooms.length > 1 && (
            <select value={room} onChange={(e) => setRoom(e.target.value)}
              className={glassFieldClass('w-auto')} aria-label="Which room">
              <option value="">Every room</option>
              {rooms.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
        </div>
        <p className="text-sm text-[var(--sp-body)]">
          <strong className="text-ink dark:text-slate-100">{totals.present}</strong> present ·{' '}
          <strong className={totals.absent ? 'text-rose-600 dark:text-rose-400' : 'text-ink dark:text-slate-100'}>
            {totals.absent}
          </strong> absent
          {totals.late ? ` · ${totals.late} late` : ''}
          {' · '}{totals.scripts} script{totals.scripts === 1 ? '' : 's'} to collect
        </p>
      </div>

      <ul className="divide-y divide-[var(--sp-line)] border-y border-[var(--sp-line)]">
        {shown.map((c) => {
          const mark = marks[c.studentId];
          const absent = mark?.status === 'absent';
          return (
            <li key={c.studentId} className={`px-4 py-2 ${absent ? 'bg-rose-50/60 dark:bg-rose-950/20' : ''}`}>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink dark:text-slate-100">
                    <span className="tabular-nums text-[var(--sp-muted)]">{c.candidateNumber}</span>{' '}
                    {c.candidateName}
                  </p>
                  <p className="text-xs text-[var(--sp-muted)]">
                    {c.className}
                    {seats[c.studentId] && (
                      <span className="text-[var(--sp-body)]">
                        {' · '}{seats[c.studentId].roomName} desk {seats[c.studentId].desk}
                      </span>
                    )}
                    {mark?.arrived && <span className="text-amber-700 dark:text-amber-400"> · arrived {mark.arrived}</span>}
                    {mark?.note && <span> · {mark.note}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button" disabled={!canFill}
                    onClick={() => setMark(c.studentId, absent ? null : { ...(mark ?? {}), status: 'absent' })}
                    className={`rounded-lg border-2 px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${
                      absent
                        ? 'border-rose-300 bg-rose-100 text-rose-800 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                        : 'border-[var(--sp-line)] text-[var(--sp-body)] hover:border-rose-300'}`}
                  >
                    {absent ? 'Absent' : 'Mark absent'}
                  </button>
                  {!absent && (
                    <button
                      type="button" disabled={!canFill}
                      onClick={() => setMark(c.studentId, { status: 'present', note: mark?.note, arrived: mark?.arrived ? undefined : nowClock() })}
                      className={`rounded-lg border-2 border-[var(--sp-line)] px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${
                        mark?.arrived ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'text-[var(--sp-body)] hover:border-amber-300'}`}
                      title="Arrived after the start"
                    >
                      <Clock className="inline h-3.5 w-3.5" /> {mark?.arrived ? 'Not late' : 'Late'}
                    </button>
                  )}
                  <button
                    type="button" disabled={!canFill}
                    onClick={() => setNoteFor(noteFor === c.studentId ? '' : c.studentId)}
                    className="rounded-lg border-2 border-[var(--sp-line)] px-2 py-1 text-xs font-semibold text-[var(--sp-body)] transition-colors hover:border-primaryLight disabled:opacity-40"
                    title="A note against this candidate"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {noteFor === c.studentId && canFill && (
                <input
                  autoFocus
                  defaultValue={mark?.note ?? ''}
                  placeholder="Illness, an irregularity, left early…"
                  onBlur={(e) => {
                    const note = e.target.value.trim();
                    setMark(c.studentId, { status: mark?.status ?? 'present', arrived: mark?.arrived, ...(note ? { note } : {}) });
                    setNoteFor('');
                  }}
                  className={glassFieldClass('mt-2')}
                />
              )}
            </li>
          );
        })}
        {!shown.length && (
          <li className="px-4 py-8 text-center text-sm text-[var(--sp-muted)]">
            {list.length ? 'Nobody by that name.' : 'No candidate is entered for this paper on the statements of entry.'}
          </li>
        )}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <p className="text-xs text-[var(--sp-muted)]">
          {locked
            ? 'This register has been taken. The office can reopen it.'
            : canFill
              ? 'Everyone not marked absent counts as present. Confirming signs it with your name.'
              : 'You are not on duty for this paper, so it is read-only.'}
        </p>
        {!locked && canFill && (
          <button
            type="button" className={btnPrimary}
            onClick={() => onSave({
              confirmedAt: new Date().toISOString(),
              confirmedBy: uid,
              confirmedByName: userName,
              totals: { entered: totals.entered, present: totals.present, absent: totals.absent },
            })}
          >
            <CheckCircle2 className="h-4 w-4" />
            Confirm — {totals.present} present, {totals.absent} absent
          </button>
        )}
      </div>
    </SoftCard>
  );
}

/** Gaborone is UTC+2 and the school has no other campus; the browser clock in
 *  the hall is the right one to read a late arrival off. */
function nowClock(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function when(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
